/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import { IProductsDimensionsAvailabilities } from '@msdyn365-commerce/commerce-entities';
import {
    getCatalogId,
    IComponent,
    IComponentProps,
    ICoreContext,
    IGridSettings,
    IImageData,
    IImageSettings,
    Image,
    msdyn365Commerce
} from '@msdyn365-commerce/core';
import { AttributeValue, Customer, ProductPrice, ProductSearchResult, Cart, AsyncResult } from '@msdyn365-commerce/retail-proxy';
// Todo: will remove getProductPageUrlSync after implentation of custom urls
import { generateImageUrl, /*getProductPageUrlSync,*/ validateCatalogId } from '@msdyn365-commerce-modules/retail-actions';
import { getPayloadObject, getTelemetryAttributes, ITelemetryContent, onTelemetryClick } from '@msdyn365-commerce-modules/utilities';
import React from 'react';

import PriceComponent, { IPriceComponentResources } from '../../../themes/krannich/views/components/price.component';
import { IPlpRequestPriceButtonData } from '../krannich-c-search-result-container.props.autogenerated';
import { ATPMessage, TrafficLights } from '../../../utils/atp-utils';
import { _calculateWpPrice, filteredProductAttributeList, formatUrlPath, isRequestPrice, taxPercentage } from '../../../utils/helper';
import { PromotionBadges } from '../../../utils/badges';

export interface IProductComponentProps extends IComponentProps<{ product?: ProductSearchResult }> {
    className?: string;
    imageSettings?: IImageSettings;
    savingsText?: string;
    freePriceText?: string;
    originalPriceText?: string;
    currentPriceText?: string;
    ratingAriaLabel?: string;
    ratingCountAriaLabel?: string;
    allowBack?: boolean;
    telemetryContent?: ITelemetryContent;
    quickViewButton?: React.ReactNode;
    productComparisonButton?: React.ReactNode;
    inventoryLabel?: string;
    isPriceMinMaxEnabled?: boolean;
    priceResources?: IPriceComponentResources;
    dimensionAvailabilities?: IProductsDimensionsAvailabilities[];
    swatchItemAriaLabel?: string;
    requestPriceBtn?: IPlpRequestPriceButtonData | undefined;
    activePrices?: ProductPrice[];
    customerInformation?: Customer;
    resources?: any;
    atpData?: any;
    cart?: AsyncResult<Cart>;
}

export interface IProductComponent extends IComponent<IProductComponentProps> {}

const PriceComponentActions = {};

/**
 * Renders product comparison button similar to the quick view button.
 * @param productComparisonButton - React element of the button.
 * @param product - Current product info.
 * @param catalogId - Current catalog.
 * @returns React element for the specific product.
 */
function renderProductComparisonButton(
    productComparisonButton: React.ReactNode,
    product: ProductSearchResult,
    catalogId: number
): JSX.Element | undefined {
    validateCatalogId(catalogId);
    return React.cloneElement(productComparisonButton as React.ReactElement, { product, catalogId });
}

const ProductCard: React.FC<IProductComponentProps> = ({
    data,
    context,
    imageSettings,
    telemetryContent,
    quickViewButton,
    productComparisonButton,
    requestPriceBtn,
    activePrices,
    customerInformation,
    resources,
    atpData,
    cart
}) => {
    const product = data.product;
    //To set Image Url
    const [imgURI, setImgURL] = React.useState<string | undefined>();
    const [producerName, setProducerName] = React.useState<string | undefined>('');
    const [isBundle, setIsBundle] = React.useState<boolean | undefined>(false);
    //to generate Image url after getting img url from api call
    React.useEffect(() => {
        if (imgURI) {
            let imageUrl = generateImageUrl(imgURI, context.actionContext.requestContext.apiSettings);
            imageUrl = imageUrl?.replace('=/', '=') || '';
            setImgURL(imageUrl);
        }
    }, [imgURI]);

    const isBundleProduct = () => {
        product?.AttributeValues?.map(e => {
            if (e.Name === 'IsBundleProduct') {
                if (e.BooleanValue === true) {
                    setIsBundle(true);
                }
            }
        });
    };
    React.useEffect(() => {
        if (product) {
            findProducerName();
            isBundleProduct();
        }
    }, [product]);
    const _renderQuickView = (quickview: React.ReactNode, item?: number, attributevalues?: AttributeValue[]): JSX.Element | null => {
        if (!quickview) {
            return null;
        }
        return React.cloneElement(quickview as React.ReactElement, {
            selectedProductId: item,
            attributevalues: attributevalues,
            cart: cart
        });
    };
    //To set the producer name
    React.useEffect(() => {
        if (producerName) {
            setImgURL(producerName);
        }
    }, [producerName]);

    /**
     * Updates the product url link to product details page.
     * @param  productDetailsPageUrl - Product page url.
     * @param  coreContext - Context of the module using the component.
     * @param  queryString - Querystring to be added to the URL.
     * @returns The update product page url.
     */
    function updateProductUrl(productDetailsPageUrl: string, coreContext: ICoreContext, queryString: string): string {
        const sourceUrl = new URL(productDetailsPageUrl, coreContext.request.apiSettings.baseUrl);
        if (sourceUrl.search) {
            sourceUrl.search += `&${queryString}`;
        } else {
            sourceUrl.search += queryString;
        }

        const updatedUrl = new URL(sourceUrl.href);
        return updatedUrl.pathname + sourceUrl.search;
    }
    const productAttributes = filteredProductAttributeList(product?.AttributeValues, context.app.config.kcProductAttribute);
    const findProducerName = () => {
        productAttributes?.map(e => {
            if (e.identifier === 'Manufacturer') {
                const manufactuerValueforImage = e.ExtensionProperties?.find(eProperty => eProperty.Key === 'globalAttributeValue')?.Value;
                setProducerName(manufactuerValueforImage?.StringValue);
            }
        });
    };
    const _renderProducerName = (prod: ProductSearchResult): string => {
        if (!prod) {
            return '';
        }
        return productAttributes?.find(attr => attr.identifier === 'Manufacturer')?.TextValue || '';
    };
    const _renderProducerKeyName = (prod: ProductSearchResult): string => {
        if (!prod) {
            return '';
        }
        return productAttributes?.find(attr => attr.identifier === 'Manufacturer')?.Name || '';
    };
    const getManufacturerItemTypeName = (prod: ProductSearchResult): string => {
        if (!prod) {
            return '';
        }
        return prod?.ExtensionProperties?.find(extension => extension.Key === 'ManufItemTypeName')?.Value?.StringValue || '';
    };
    // Todo: will remove productUrl after implentation of custom urls
    // let productUrl = product
    //     ? getProductPageUrlSync(
    //           formatUrlPath(_renderProducerName(product), getManufacturerItemTypeName(product)) ?? '',
    //           product.RecordId,
    //           context.actionContext,
    //           undefined
    //       )
    //     : '';

    const urlPath = formatUrlPath(_renderProducerName(product!), getManufacturerItemTypeName(product!));
    const pathName = context.actionContext.requestContext.url.requestUrl.pathname.split('/');
    const isAddingPathWithUrl = context.actionContext.requestContext.app.config.addPathWithUrl ?? false;
    let productUrl =
        product &&
        (isAddingPathWithUrl
            ? `${context.actionContext.requestContext.url.requestUrl.origin}/${pathName[1]}/${urlPath}/${product.RecordId}.p`
            : `${context.actionContext.requestContext.url.requestUrl.origin}/${urlPath}/${product.RecordId}.p`);
    if (productUrl) {
        productUrl = updateProductUrl(productUrl, context, 'back=true');
    }

    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment,@typescript-eslint/no-unsafe-member-access -- app context is generic

    if (!product) {
        return null;
    }

    // Construct telemetry attribute to render
    const payLoad = getPayloadObject('click', telemetryContent!, '', product.RecordId.toString());

    const attribute = getTelemetryAttributes(telemetryContent!, payLoad);
    //use this function to render or display category image
    const _renderCategoryImg = (imgURI: string) => {
        return (
            <div className='_logo-container'>
                <img src={`${imgURI}.png`} alt='producer-images' className='_logo' />
            </div>
        );
    };

    const _renderProductPlacementImage = (
        imageSettings?: IImageSettings,
        gridSettings?: IGridSettings,
        imageUrl?: string,
        altText?: string
    ): JSX.Element | null => {
        if (!imageUrl || !gridSettings || !imageSettings) {
            return null;
        }
        const img: IImageData = {
            src: imageUrl,
            altText: altText ? altText : ''
        };
        const imageProps = {
            gridSettings: gridSettings,
            imageSettings
        };
        return <Image {...img} {...imageProps} loadFailureBehavior='empty' />;
    };

    const activePrice = activePrices?.find((val: any) => val?.ProductId === product?.RecordId);
    const trafficLightATP = atpData && atpData?.filter((item: any) => item?.ItemId === product?.ItemId);
    const atpMsgRes = trafficLightATP && trafficLightATP[0]?.JsonResponseObject;
    const atpMsgResParsed = atpMsgRes && JSON.parse(atpMsgRes);
    const extensionsProps = product?.ExtensionProperties;
    const customerTaxPercentage = taxPercentage(customerInformation!);
    let promotionBadge = '';
    if (extensionsProps?.find(e => e?.Key === 'PromotionBadge')) {
        promotionBadge = extensionsProps?.find(e => e?.Key === 'PromotionBadge')?.Value?.StringValue || '';
    }
    return (
        <div className={promotionBadge?.length ? 'ks-plp-tile ks-plp-tile-promotion' : 'ks-plp-tile'} key={product.RecordId}>
            <div className='msc-product-tile-cart-icon'></div>
            <a
                href={productUrl}
                onClick={onTelemetryClick(telemetryContent!, payLoad, product.Name!)}
                className='msc-product'
                {...attribute}
                key={product.RecordId}
            >
                <div className='plp-img-wrapper'>
                    <div className='product-card-header'>
                        <PromotionBadges context={context} promotionalBadge={promotionBadge} />
                        <div className='producer__ImageURI'>{imgURI && _renderCategoryImg(imgURI)}</div>
                    </div>
                    <div className='msc-product__image'>
                        {_renderProductPlacementImage(imageSettings, context.request.gridSettings, product.PrimaryImageUrl, product.Name)}
                    </div>
                </div>
            </a>
            <div className='plp-tile-details'>
                <a
                    href={productUrl}
                    onClick={onTelemetryClick(telemetryContent!, payLoad, product.Name!)}
                    className='msc-product'
                    {...attribute}
                    key={product.RecordId}
                >
                    <div className='msc-product__details'>
                        <h4 className='msc-product__title'>{product.Name}</h4>

                        <div className='msc-product__item_number'>
                            <span>{resources.itemNumberLabel}</span>
                            <span> {product.ItemId}</span>
                        </div>

                        <div className='msc-product__item_producer'>
                            <span className='manufacturer-name_plp'>
                                <div>
                                    <span>{_renderProducerKeyName(product)}:</span> <span>{_renderProducerName(product)}</span>
                                </div>
                            </span>
                        </div>
                    </div>
                    {context.actionContext.requestContext.user.isAuthenticated && trafficLightATP && (
                        <div className='lights-section'>
                            <TrafficLights
                                lightColor={isBundle ? 'Yellow' : trafficLightATP[0]?.TrafficLight}
                                isVisible={atpMsgResParsed?.IsVisible}
                            />
                            {isBundle ? (
                                <h4 className='_traffic-light-quantity'>{resources.atpDiscontinuedMessageLabel} </h4>
                            ) : (
                                /*VSI CUSTOMIZATION | DATE FORMATE | START | */
                                atpMsgResParsed && (
                                    <ATPMessage
                                        {...atpMsgResParsed}
                                        locale={context.app.config.dateFormat}
                                        currency={context.request.channel?.Currency}
                                    />
                                )
                                /*VSI CUSTOMIZATION | DATE FORMATE | END | */
                            )}
                        </div>
                    )}
                </a>
                <div
                    className={`request-price_plp ${
                        context.actionContext.requestContext.user.isAuthenticated ? 'request-price_plp-border' : null
                    } `}
                >
                    {context.actionContext.requestContext.user.isAuthenticated ? (
                        <>
                            <div className='_price-box'>
                                <div>
                                    {isRequestPrice(activePrice!, customerInformation?.LineDiscountGroup!, isBundle) || isBundle ? (
                                        <div className='no-price-wrapper'>
                                            <span className='label'>{resources.requestPriceLabel}</span>
                                            <div className='tooltip-wrapper' tabIndex={0}>
                                                <span className='tooltip-icon' />
                                                <span className='tooltip-text'>{resources.tooltipText}</span>
                                            </div>
                                        </div>
                                    ) : (
                                        <>
                                            <PriceComponent
                                                id={String(activePrice?.ProductId)}
                                                typeName={'krannich-c-search-result-container'}
                                                data={{ price: activePrice! }}
                                                className='product-price'
                                                freePriceText={resources?.priceFree}
                                                originalPriceText={resources?.originalPriceText}
                                                currentPriceText={resources?.currentPriceText}
                                                context={context}
                                                isIncExPriceFormatEnabled={true}
                                                customerTaxPercentage={customerTaxPercentage}
                                            />
                                        </>
                                    )}
                                </div>
                                <div className='msc-product__item_wpprice'>
                                    {context.actionContext.requestContext.user.isAuthenticated &&
                                        _calculateWpPrice(product?.AttributeValues!, context, isBundle, activePrice!, customerInformation!)}
                                </div>
                            </div>

                            {productComparisonButton &&
                                renderProductComparisonButton(productComparisonButton, product, getCatalogId(context.request))}
                            <div className='list-product-quick-view'>
                                {quickViewButton && _renderQuickView(quickViewButton, product.RecordId, product?.AttributeValues)}
                                <a
                                    href={productUrl}
                                    onClick={onTelemetryClick(telemetryContent!, payLoad, product.Name!)}
                                    className='msc-product product-info__detail-btn'
                                    {...attribute}
                                >
                                    {resources.viewDetailsLabel}
                                </a>
                            </div>
                        </>
                    ) : (
                        <div className='request-price'>
                            <a
                                title='Request Price'
                                className='Request-Price-btn  clicks'
                                href={`${requestPriceBtn?.linkUrl.destinationUrl}?ItemId=${product.ItemId}&Name=${
                                    product.Name
                                }&producerKeyName=${_renderProducerKeyName(product)}&producer=${_renderProducerName(product)}`}
                            >
                                {requestPriceBtn?.linkText}
                            </a>
                            <a
                                href={productUrl}
                                onClick={onTelemetryClick(telemetryContent!, payLoad, product.Name!)}
                                className='msc-product product-info__detail-btn'
                                {...attribute}
                            >
                                {resources.viewDetailsLabel}
                            </a>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export const ProductComponent: React.FunctionComponent<IProductComponentProps> = msdyn365Commerce.createComponentOverride<
    IProductComponent
>('Product', { component: ProductCard, ...PriceComponentActions });

export default ProductComponent;
